import {
  ReactElement, memo, useCallback, useState, useMemo,
} from 'react';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import cn from 'classnames';
import { atcb_action as ATCBAction } from 'add-to-calendar-button';
// import 'add-to-calendar-button/assets/css/atcb.css';

import { useAppContext } from 'lib/AppContext';

import { useDispatcher } from 'store/utils/redux/hooks';
import * as invitationStore from 'store/nodes/invitation';
import * as cardStore from 'store/nodes/card';

import Button from 'components/Button';
import TextInput from 'components/TextInput';
import MultilineInput from 'components/MultilineInput';

import { ReactComponent as CalendarIcon } from '../assets/calendar.svg';

type CalendarOptions = {
  type: ('Apple' | 'Google' | 'iCal' | 'Microsoft365' | 'MicrosoftTeams' | 'Outlook.com' | 'Yahoo')[],
  id: string | null,
};

const Note = (): ReactElement => {
  const classes = useStyles();
  const dispatcher = useDispatcher();
  const { bowser } = useAppContext();

  const calendarOptions = useMemo((): CalendarOptions => {
    const isSafari = bowser.browser.name?.toLowerCase() === 'safari';
    const isChrome = bowser.browser.name?.toLowerCase() === 'chrome';
    const isAndroid = bowser.os.name?.toLowerCase() === 'android';
    const isIos = bowser.os.name?.toLowerCase() === 'ios';
    const isMacos = bowser.os.name?.toLowerCase() === 'macos';

    if (
      isAndroid
      || ((isIos || isMacos) && isChrome)
    ) {
      return {
        type: ['Google'],
        id: 'atcb-btn-custom-google',
      };
    }
    if ((isIos || isMacos) && isSafari) {
      return {
        type: ['Apple'],
        id: 'atcb-btn-custom-apple',
      };
    }
    return {
      type: ['Apple', 'Google', 'iCal', 'Microsoft365', 'MicrosoftTeams', 'Outlook.com', 'Yahoo'],
      id: null,
    };
  }, []);

  const eventData = useSelector(invitationStore.selectors.getEvent);
  const rsvpData = useSelector(invitationStore.selectors.getRSVP);
  const isShareableLink = useSelector(cardStore.selectors.isShareableLink);
  const isLoadingNote = useSelector(invitationStore.selectors.isLoading('note'));

  const [name, setName] = useState<string>('');
  const [note, setNote] = useState<string>('');

  const isStatusAttending = rsvpData?.status === 'attending';

  const [isFieldValid, setFieldValid] = useState<Record<'name', boolean>>({
    name: true,
  });

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    setFieldValid({
      ...isFieldValid,
      name: true,
    });
  }, [isFieldValid]);

  const validate = useCallback(() => {
    if (!(!isStatusAttending && isShareableLink)) {
      return true;
    }
    const newErrorField = { ...isFieldValid };
    if (!name) {
      newErrorField.name = false;
    }
    setFieldValid(newErrorField);
    return Object.values(newErrorField).reduce((result, value) => {
      if (!result) {
        return false;
      }
      return value;
    }, true);
  }, [isStatusAttending, isShareableLink, name, isFieldValid]);

  const handleNoteChange = useCallback((value: string) => {
    setNote(value);
  }, []);

  const handleBackToInfo = useCallback(() => {
    dispatcher.invitation.setStep('info');
  }, []);

  const handleBackToAttending = useCallback(() => {
    dispatcher.invitation.setStep('attending');
  }, []);

  const handleFinish = useCallback(() => {
    const isValid = validate();
    if (!isValid) {
      return;
    }
    dispatcher.invitation.sendNote(note, name);
  }, [note, name, validate]);

  const handleAddCalendar = useCallback(() => {
    const config = {
      name: eventData?.title || '',
      location: eventData?.locationAddress || '',
      startDate: DateTime.fromISO(eventData?.startTime as string, { locale: 'en' }).toFormat('yyyy-MM-dd'),
      startTime: DateTime.fromISO(eventData?.startTime as string, { locale: 'en' }).toFormat('hh:mm'),
      options: calendarOptions.type,
      timeZone: 'currentBrowser',
    };
    if (eventData?.endTime) {
      (config as any).endDate = DateTime.fromISO(eventData?.endTime as string, { locale: 'en' }).toFormat('yyyy-MM-dd');
      (config as any).endTime = DateTime.fromISO(eventData?.endTime as string, { locale: 'en' }).toFormat('hh:mm');
    } else {
      (config as any).endDate = DateTime.fromISO(eventData?.startTime as string, { locale: 'en' }).plus({ hour: 1 }).toFormat('yyyy-MM-dd');
      (config as any).endTime = DateTime.fromISO(eventData?.startTime as string, { locale: 'en' }).plus({ hour: 1 }).toFormat('hh:mm');
    }
    ATCBAction(config);
    if (!calendarOptions.id) {
      return;
    }
    const overlay = document.querySelector('#atcb-bgoverlay');
    if (overlay instanceof HTMLDivElement) {
      overlay.style.display = 'none';
    }
    const element = document.querySelector(`[id="${calendarOptions.id}"]`);
    if (element instanceof HTMLDivElement) {
      element.click();
    }
  }, [eventData]);

  return (
    <div className={classes.container}>
      <div className={classes.title}>{eventData?.title}</div>
      {eventData?.startTime && (
        <div className={classes.blockDate}>{DateTime.fromISO(eventData?.startTime, { locale: 'en' }).toLocaleString(DateTime.DATETIME_MED)}</div>
      )}
      {!isStatusAttending && isShareableLink && (
        <TextInput
          label="Your Name:"
          value={name}
          onChange={handleNameChange}
          className={classes.input}
          hasError={!isFieldValid.name}
          errorText="Name is required"
        />
      )}
      <MultilineInput
        label="Leave a note (optional):"
        value={note}
        onChange={handleNoteChange}
        className={cn(classes.multilineInput, classes.input)}
      />
      {isStatusAttending && (
        <div className={classes.blockCalendar}>
          <CalendarIcon fill="#20B2A4" />
          <div onClick={handleAddCalendar}>Add it to your calendar</div>
        </div>
      )}
      {isStatusAttending && (
        <div className={classes.buttons}>
          <Button
            width="full"
            height={44}
            htmlColor="#F2F2F2"
            htmlTextColor="#3F3F3F"
            isDisabled={isLoadingNote}
            onPress={handleBackToInfo}
          >
            Back
          </Button>
          <Button
            width="full"
            height={44}
            htmlColor="#20B2A4"
            isLoading={isLoadingNote}
            onPress={handleFinish}
          >
            Done
          </Button>
        </div>
      )}
      {!isStatusAttending && (
        <div className={classes.buttons}>
          <Button
            width="full"
            height={44}
            htmlColor="#F2F2F2"
            htmlTextColor="#3F3F3F"
            isDisabled={isLoadingNote}
            onPress={handleBackToAttending}
          >
            Back
          </Button>
          <Button
            width="full"
            height={44}
            htmlColor="#868686"
            isLoading={isLoadingNote}
            onPress={handleFinish}
          >
            Done
          </Button>
        </div>
      )}
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    height: '42rem',
  },
  title: {
    fontWeight: '700',
    fontSize: '2.2rem',
    lineHeight: 1.3,
    textAlign: 'left',
    color: '#454545',
  },
  multilineInput: {
    flex: 1,
  },
  input: {
    '&': {
      marginTop: '2.8rem',
    },
    '& + &': {
      marginTop: '0.6rem',
    },
  },
  blockDate: {
    marginTop: '1rem',
    fontSize: '1.5rem',
    color: '#3F3F3F',
  },
  blockCalendar: {
    '&': {
      display: 'flex',
      marginTop: '2rem',
      color: '#20B2A4',
      fontWeight: 600,
      fontSize: '1.4rem',
      justifyContent: 'stretch',
      alignItems: 'center',
    },
    '& > svg': {
      width: '2.6rem',
      height: '2.8rem',
      marginRight: '1rem',
      marginLeft: '0.5rem',
    },
    '& > div': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  buttons: {
    '&': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: '2.4rem',
    },
    '& > button': {
      marginRight: '1.6rem',
    },
    '& > button:last-child': {
      marginRight: 0,
    },
  },
});

export default memo(Note);
